import { useEffect, useState } from "react";
import { Trophy, Info, ExternalLink } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { api, errMsg } from "../lib/api";
import { fmtUsd, short } from "../lib/format";
import { addrUrl } from "../lib/chains";
import { Panel, Loading, ErrorState, Empty, Unavailable } from "./State";

export const TopTraders = ({ pool, compact = true }) => {
  const [win, setWin] = useState("24h");
  const [s, setS] = useState({ loading: true, data: null, error: null });
  const load = () => {
    setS((x) => ({ ...x, loading: true, error: null }));
    api.get("/leaderboard", { params: { pool, window: win } }).then((r) => setS({ loading: false, data: r.data, error: null }))
      .catch((e) => setS({ loading: false, data: null, error: errMsg(e) }));
  };
  useEffect(load, [pool, win]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Panel testid="traders-leaderboard" title="Top 10 traders" icon={Trophy} right={
      <Tooltip><TooltipTrigger asChild><button data-testid="traders-methodology" className="text-slate-500 hover:text-lime"><Info className="h-4 w-4" /></button></TooltipTrigger>
        <TooltipContent className="max-w-xs bg-[#1A202C] text-slate-200 border-lime/20 text-xs">{s.data?.methodology || "Ranked by indexed on-chain swap volume. Not profit."}</TooltipContent></Tooltip>}>
      <div className="flex gap-1 px-5 pt-3">
        {["24h", "7d", "30d"].map((w) => (
          <button key={w} data-testid={`traders-filter-${w}`} onClick={() => setWin(w)} className={`flex-1 rounded py-1 text-[11px] font-mono font-semibold ${win === w ? "bg-lime text-black" : "bg-white/5 text-slate-400 hover:text-white"}`}>{w}</button>
        ))}
      </div>
      <div className="px-3 py-3" data-testid="traders-leaderboard-table">
        {s.loading ? <Loading /> : s.error ? <ErrorState message={s.error} onRetry={load} /> : !s.data.available ? (
          <div className="px-2"><Unavailable testid="traders-unavailable" title={`${win} ranking unavailable`} reason={s.data.reason} /></div>
        ) : !s.data.traders.length ? <Empty title="No swaps indexed in this window" /> : (
          <ol className="space-y-1">
            {s.data.traders.map((t) => (
              <li key={t.address} data-testid={`trader-row-${t.rank}`} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-lime/5">
                <span className={`hex h-7 w-7 grid place-items-center text-[11px] font-black ${t.rank <= 3 ? "bg-lime text-black" : "bg-white/5 text-slate-400"}`}>{t.rank}</span>
                <div className="flex-1 min-w-0">
                  <a href={addrUrl(t.address)} target="_blank" rel="noreferrer" className="text-xs font-mono text-slate-200 hover:text-lime flex items-center gap-1">
                    {t.alias || short(t.address)} <ExternalLink className="h-3 w-3 opacity-50" />
                  </a>
                  <p className="text-[10px] text-slate-500">{t.trades} swaps · {t.buys}B / {t.sells}S</p>
                </div>
                <span className="text-xs font-mono font-semibold text-lime">{fmtUsd(t.volume_usd)}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
      {s.data?.available && <p className="px-5 pb-4 text-[10px] text-slate-600 font-mono">Volume from {s.data.sample_size} latest indexed swaps in pool {short(s.data.pool)}. Volume ≠ profit.</p>}
      {!compact && s.data && <p className="px-5 pb-5 text-xs text-slate-400 leading-relaxed">{s.data.methodology}</p>}
    </Panel>
  );
};
